import React from 'react';
import Select from '../../../components/Select/Select';
import Input from '../../../components/Input/Input';
import InputText from '../../../components/Input/InputText';
import TextArea from '../../../components/TextArea/TextArea';
import Checkbox from '../../../components/Checkbox/Checkbox';
import DealershipAccountField from '../../../components/DealershipAccountField/DealershipAccountField';

const renderVehicleCatalogInfo = (formik, vehicleRequired) => {
  const asterisk = vehicleRequired ? <span className="text-blue-100">*</span> : '';

  return (
    <div className="mb-4">
      <p className="block font-sans text-sm font-bold text-gray-600 mb-2">Vehicle{asterisk}</p>
      <Select
        label="Year"
        id="vehicle.year"
        value={formik.values?.vehicle?.year ?? ''}
        disabled
        options={[{ value: formik.values?.vehicle?.year, label: formik.values?.vehicle?.year }]}
        error={formik.touched?.vehicle?.year && formik.errors?.vehicle?.year}
        inline
        searchable
        dataTest="generated-prompt-form-vehicle-year"
      />

      <Select
        label="Make"
        id="vehicle.make"
        value={formik.values?.vehicle?.make ?? ''}
        disabled
        options={[{ value: formik.values?.vehicle?.make, label: formik.values?.vehicle?.make }]}
        error={formik.touched?.vehicle?.make && formik.errors?.vehicle?.make}
        inline
        searchable
        dataTest="generated-prompt-form-vehicle-make"
      />

      <Select
        label="Model"
        id="vehicle.model"
        value={formik.values?.vehicle?.model ?? ''}
        disabled
        options={[{ value: formik.values?.vehicle?.model, label: formik.values?.vehicle?.model }]}
        inline
        searchable
        dataTest="generated-prompt-form-vehicle-model"
      />
    </div>
  );
};

const BlogPartial = ({ formik, vehicleRequired = false }) => {
  return (
    <div className="grid gap-6 grid-cols-2">
      <div className="max-w-[18rem]">
        {renderVehicleCatalogInfo(formik, vehicleRequired)}

        <DealershipAccountField
          label="Dealership Account Id"
          id="dealershipAccount"
          value={formik.values?.dealershipAccount}
          disabled
          error={formik.touched?.dealershipAccount && formik.errors?.dealershipAccount?.id}
          maxLength={50}
          dataTest="mslp-form-dealership-account-id"
          bottomMarginSizing={1}
        />

        <TextArea
          label="Content Topic"
          id="contentTopic"
          value={formik.values?.contentTopic}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          required
          dataTest="generated-prompt-form-content-topic"
          error={formik.touched?.contentTopic && formik.errors?.contentTopic}
          disabled
          bottomMarginSizing={1}
        />
        <div className="grid grid-rows-1 grid-cols-form-word-copyscape gap-6 mb-4">
          <div className="row-start-1 row-end-2 col-start-1 col-end-2 w-full max-w-[128px]">
            <Input
              label="Word Count"
              value={formik.values?.wordCount}
              onChange={({ target: { value } }) => {
                formik.setFieldValue('wordCount', value.replace(/[^\d]/, ''));
              }}
              onBlur={formik.handleBlur}
              error={formik.touched?.wordCount && formik.errors?.wordCount}
              id="wordCount"
              maxLength={4}
              required
              dataTest="generated-prompt-form-word-count"
              type="tel"
              max="2500"
              min="100"
              pattern="[0-9]{1,4}"
              disabled
            />
          </div>
          <div className="row-start-1 row-end-2 col-start-2 col-end-3 flex justify-end whitespace-nowrap">
            <Checkbox
              label="Check with Copyscape"
              value={formik.values?.executeCopyscapeSearch}
              onChange={({ target: { checked } }) => {
                formik.setFieldValue('executeCopyscapeSearch', checked);
              }}
              onBlur={formik.handleBlur}
              type="checkbox"
              error={
                formik.touched?.executeCopyscapeSearch && formik.errors?.executeCopyscapeSearch
              }
              id="copyscape"
              dataTest="mslp-form-copyscape"
            />
          </div>
        </div>

        <TextArea
          label="Target Audience or Market"
          id="targetAudience"
          value={formik.values?.targetAudience}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          dataTest="generated-prompt-form-target-audience"
          error={formik.touched?.targetAudience && formik.errors?.targetAudience}
          bottomMarginSizing={1}
        />

        <TextArea
          label="Geos"
          id="geos"
          value={formik.values?.geos}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          dataTest="generated-prompt-form-geos"
          error={formik.touched?.geos && formik.errors?.geos}
          bottomMarginSizing={0}
        />
      </div>
      <div className="-mt-2 max-w-[18rem]">
        <InputText
          label="Content Example 1"
          id="contentOne.url"
          value={formik.values?.contentOne.url}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled
          dataTest="generated-prompt-form-content-one-url"
          error={formik.touched?.contentOne?.url && formik.errors?.contentOne?.url}
          placeholder="ex. https://www.dealer.com/"
        />
        <InputText
          id="contentOne.writeStyle"
          value={formik.values?.contentOne.writeStyle}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled
          dataTest="generated-prompt-form-content-one-write-style"
          error={formik.touched?.contentOne?.writeStyle && formik.errors?.contentOne?.writeStyle}
          placeholder="What to take from example ex. tone, style, etc."
          bottomMarginSizing={1}
        />

        <InputText
          label="Content Example 2"
          id="contentTwo.url"
          value={formik.values?.contentTwo.url}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled
          dataTest="generated-prompt-form-content-two-url"
          error={formik.touched?.contentTwo?.url && formik.errors?.contentTwo?.url}
          placeholder="ex. https://www.dealer.com/"
        />
        <InputText
          id="contentTwo.writeStyle"
          value={formik.values?.contentTwo.writeStyle}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled
          dataTest="generated-prompt-form-content-two-write-style"
          error={formik.touched?.contentTwo?.writeStyle && formik.errors?.contentTwo?.writeStyle}
          placeholder="What to take from example ex. tone, style, etc."
          bottomMarginSizing={1}
        />

        <TextArea
          label="Additional Information"
          id="additionalInformation"
          value={formik.values?.additionalInformation}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          dataTest="generated-prompt-form-additional-information"
          error={formik.touched?.additionalInformation && formik.errors?.additionalInformation}
          bottomMarginSizing={1}
        />

        <TextArea
          label="Negative Prompt"
          id="negativePrompt"
          value={formik.values?.negativePrompt}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          dataTest="generated-prompt-form-negative-prompt"
          error={formik.touched?.negativePrompt && formik.errors?.negativePrompt}
          bottomMarginSizing={1}
        />

        <InputText
          label="Link 1"
          id="linkOne.url"
          value={formik.values?.linkOne.url}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          dataTest="generated-prompt-form-link-one-url"
          error={formik.touched?.linkOne?.url && formik.errors?.linkOne?.url}
          placeholder="ex. https://www.dealer.com/"
        />
        <InputText
          id="linkOne.description"
          value={formik.values?.linkOne.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          dataTest="generated-prompt-form-link-one-description"
          error={formik.touched?.linkOne?.description && formik.errors?.linkOne?.description}
          placeholder="Add link description"
          bottomMarginSizing={1}
        />

        <InputText
          label="Link 2"
          id="linkTwo.url"
          value={formik.values?.linkTwo.url}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          dataTest="generated-prompt-form-link-two-url"
          error={formik.touched?.linkTwo?.url && formik.errors?.linkTwo?.url}
          placeholder="ex. https://www.dealer.com/"
        />
        <InputText
          id="linkTwo.description"
          value={formik.values?.linkTwo.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          dataTest="generated-prompt-form-link-two-description"
          error={formik.touched?.linkTwo?.description && formik.errors?.linkTwo?.description}
          placeholder="Add link description"
          bottomMarginSizing={0}
        />
      </div>
    </div>
  );
};

export default BlogPartial;
